import { useState, useEffect, useRef } from 'react';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toIso(year, monthZeroBased, day) {
  return `${year}-${pad2(monthZeroBased + 1)}-${pad2(day)}`;
}

function parseIso(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  return { year: y, month0: m - 1, day: d };
}

export default function CalendarPopover({ value, onChange, minDate, maxDate, onClose }) {
  const today = new Date();
  const parsed = parseIso(value) || parseIso(maxDate) || { year: today.getFullYear(), month0: today.getMonth(), day: today.getDate() };

  const [viewYear, setViewYear] = useState(parsed.year);
  const [viewMonth, setViewMonth] = useState(parsed.month0);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleKey(e) {
      /* c8 ignore start */
      if (e.key === 'Escape') onClose?.();
      /* c8 ignore stop */
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    function handleOutside(e) {
      /* c8 ignore start */
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.();
      /* c8 ignore stop */
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [onClose]);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const maxIso = maxDate;
  const minIso = minDate;

  const minYear = (() => {
    const p = parseIso(minIso);
    return p ? p.year : today.getFullYear() - 10;
  })();
  const maxYear = (() => {
    const p = parseIso(maxIso);
    return p ? p.year : today.getFullYear() + 1;
  })();

  const daysInMonth = (y, m0) => new Date(y, m0 + 1, 0).getDate();

  const firstWeekday = (y, m0) => new Date(y, m0, 1).getDay();
  const isDisabled = (iso) => {
    if (minIso && iso < minIso) return true;
    if (maxIso && iso > maxIso) return true;
    return false;
  };

  const handlePick = (day) => {
    const iso = toIso(viewYear, viewMonth, day);
    /* c8 ignore start */
    if (isDisabled(iso)) return;
    /* c8 ignore stop */
    onChange?.(iso);
    onClose?.();
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const totalDays = daysInMonth(viewYear, viewMonth);
  const startBlank = firstWeekday(viewYear, viewMonth);

  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div ref={rootRef} className="absolute right-0 mt-2 w-64 bg-dark-800 border border-dark-600 rounded-lg shadow-lg p-3 z-50 text-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="calendar-month">Month</label>
          <select
            id="calendar-month"
            value={viewMonth}
            onChange={(e) => setViewMonth(Number(e.target.value))}
            className="bg-dark-800 border border-dark-600 text-sm text-white p-1 rounded"
          >
            {monthNames.map((m, idx) => (
              <option key={m} value={idx}>{m}</option>
            ))}
          </select>

          <label className="sr-only" htmlFor="calendar-year">Year</label>
          <select
            id="calendar-year"
            value={viewYear}
            onChange={(e) => setViewYear(Number(e.target.value))}
            className="bg-dark-800 border border-dark-600 text-sm text-white p-1 rounded"
          >
            {Array.from({ length: Math.max(1, maxYear - minYear + 1) }).map((_, i) => {
              const y = minYear + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" onClick={prevMonth} className="p-1 rounded hover:bg-dark-700">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button type="button" aria-label="Next month" onClick={nextMonth} className="p-1 rounded hover:bg-dark-700">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs text-gray-300 mb-1">
        <div className="text-center">Su</div>
        <div className="text-center">Mo</div>
        <div className="text-center">Tu</div>
        <div className="text-center">We</div>
        <div className="text-center">Th</div>
        <div className="text-center">Fr</div>
        <div className="text-center">Sa</div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startBlank }).map((_, i) => (
          <div key={`b-${i}`} className="h-8" />
        ))}

        {Array.from({ length: totalDays }).map((_, i) => {
          const d = i + 1;
          const iso = toIso(viewYear, viewMonth, d);
          const disabled = isDisabled(iso);
          const isToday = iso === todayIso;
          const selected = value === iso;

          return (
            <button
              key={iso}
              type="button"
              onClick={() => handlePick(d)}
              disabled={disabled}
              className={`h-8 flex items-center justify-center rounded ${selected ? 'bg-primary text-white' : isToday ? 'bg-dark-700 text-white' : disabled ? 'text-gray-500' : 'hover:bg-dark-700 text-gray-200'}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
